import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Popover, Switch, Button } from 'antd';
import { SparklesIcon, CheckIcon, XIcon } from '@heroicons/react/outline';
import { colors } from '../../design-system/tokens/colors';
import BoringAvatar from 'boring-avatars';

interface CompanionSettingsPopoverProps {
  companionName: string;
  companionId: string;
  companionColor: string;
  enabled: boolean;
  tableGoal: string;
  onEnabledChange: (enabled: boolean) => void;
  onSave: (enabled: boolean, tableGoal: string) => Promise<void>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

const PopoverContent = styled.div`
  width: 360px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${colors.border.secondary};
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeaderTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

const HeaderSubtitle = styled.div`
  font-size: 12px;
  color: ${colors.text.tertiary};
  margin-top: 2px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.text.secondary};
  display: block;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px 12px;
  border: 1px solid ${colors.border.primary};
  border-radius: 6px;
  font-size: 12px;
  font-family: inherit;
  color: ${colors.text.primary};
  resize: vertical;
  outline: none;
  transition: border-color 0.15s ease;
  
  &:focus {
    border-color: ${colors.brand.primary};
  }
  
  &::placeholder {
    color: ${colors.text.disabled};
  }
`;

const ToggleSection = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-top: 8px;
  border-top: 1px solid ${colors.border.secondary};
`;

const ToggleText = styled.div`
  flex: 1;
`;

const ToggleTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
  margin-bottom: 4px;
`;

const ToggleDescription = styled.div`
  font-size: 11px;
  color: ${colors.text.tertiary};
  line-height: 1.5;
`;

const HelpText = styled.div`
  font-size: 11px;
  color: ${colors.text.tertiary};
  line-height: 1.5;
  padding: 8px 12px;
  background: ${colors.background.secondary};
  border-radius: 6px;
  display: flex;
  gap: 8px;
  
  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    margin-top: 2px;
    color: ${colors.brand.primary};
  }
`;

const ButtonSection = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid ${colors.border.secondary};
`;

const ActionButton = styled(Button)<{ $variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  height: 32px;
  padding: 0 12px;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

export const CompanionSettingsPopover: React.FC<CompanionSettingsPopoverProps> = ({
  companionName,
  companionId,
  companionColor,
  enabled,
  tableGoal,
  onEnabledChange,
  onSave,
  open,
  onOpenChange,
  children
}) => {
  // Local state for editing
  const [localEnabled, setLocalEnabled] = useState(enabled);
  const [localTableGoal, setLocalTableGoal] = useState(tableGoal);
  const [isSaving, setIsSaving] = useState(false);
  
  // Sync local state when props change
  useEffect(() => {
    setLocalEnabled(enabled);
    setLocalTableGoal(tableGoal);
  }, [enabled, tableGoal]);
  
  // Reset local state when popover opens
  useEffect(() => {
    if (open) {
      setLocalEnabled(enabled);
      setLocalTableGoal(tableGoal);
    }
  }, [open, enabled, tableGoal]);
  
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(localEnabled, localTableGoal);
      // Also call onEnabledChange for immediate UI feedback
      onEnabledChange(localEnabled);
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving companion settings:', error);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleCancel = () => {
    // Reset to original values
    setLocalEnabled(enabled);
    setLocalTableGoal(tableGoal);
    onOpenChange(false);
  };
  
  const hasChanges = localEnabled !== enabled || localTableGoal !== tableGoal;
  const content = (
    <PopoverContent>
      <Header>
        <Avatar>
          <BoringAvatar
            size={32}
            name={companionId}
            variant="beam"
            colors={[companionColor, `${companionColor}dd`, `${companionColor}bb`, `${companionColor}99`, '#f3f4f6']}
          />
        </Avatar>
        <HeaderText>
          <HeaderTitle>{companionName}</HeaderTitle>
          <HeaderSubtitle>AI Assistant</HeaderSubtitle>
        </HeaderText>
      </Header>

      <Section>
        <SectionLabel htmlFor="table-goal">
          What information do you need from creators to complete this table?
        </SectionLabel>
        <TextArea
          id="table-goal"
          value={localTableGoal}
          onChange={(e) => setLocalTableGoal(e.target.value)}
          placeholder="E.g., Need shipping addresses and rates from all creators, or need confirmation they'll post by Friday..."
        />
      </Section>

      <HelpText>
        <SparklesIcon />
        <span>
          {companionName} will draft personalized messages to creators asking for the specific information you need to complete this table.
        </span>
      </HelpText>

      <ToggleSection>
        <ToggleText>
          <ToggleTitle>Enable {companionName}</ToggleTitle>
          <ToggleDescription>
            Show companion column to draft messages for each creator
          </ToggleDescription>
        </ToggleText>
        <Switch
          checked={localEnabled}
          onChange={setLocalEnabled}
        />
      </ToggleSection>

      <ButtonSection>
        <ActionButton onClick={handleCancel}>
          <XIcon />
          Cancel
        </ActionButton>
        <ActionButton 
          type="primary" 
          onClick={handleSave}
          loading={isSaving}
          disabled={!hasChanges}
        >
          <CheckIcon />
          Save
        </ActionButton>
      </ButtonSection>
    </PopoverContent>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={onOpenChange}
      placement="bottomLeft"
      overlayStyle={{ maxWidth: '400px' }}
    >
      {children}
    </Popover>
  );
};

